import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Colors } from "../../../assets/Colors";
import { GrafikServis, Lightbox, ModalPaketBanyak, PaketServisEditor, SINGKAT, pesanError } from "./AkomodasiLayanan";

const API = process.env.REACT_APP_API_URL;
const CARD_W = 132;
const CARD_H = 92;
const SNAP = 10;
const ZOOM_MIN = 0.3;
const ZOOM_MAX = 1.5;
const BLOK_MIN = 80;
const TINGGI_JUDUL_BLOK = 30;

const WARNA_BLOK = {
  abu: { bg: "rgba(156,163,175,0.12)", border: "#9ca3af", label: "Abu-abu" },
  biru: { bg: "rgba(59,130,246,0.10)", border: "#60a5fa", label: "Biru" },
  hijau: { bg: "rgba(34,197,94,0.10)", border: "#4ade80", label: "Hijau" },
  kuning: { bg: "rgba(245,158,11,0.12)", border: "#fbbf24", label: "Kuning" },
  merah: { bg: "rgba(239,68,68,0.10)", border: "#f87171", label: "Merah" },
  ungu: { bg: "rgba(168,85,247,0.10)", border: "#c084fc", label: "Ungu" },
};

// Pusat kartu room ada di dalam blok? (menentukan room mana yang ikut tergeser bersama blok)
function didalamBlok(pos, b) {
  const cx = pos.x + CARD_W / 2;
  const cy = pos.y + CARD_H / 2;
  return cx >= b.x && cx <= b.x + b.w && cy >= b.y && cy <= b.y + b.h;
}

function headers() {
  const user = JSON.parse(localStorage.getItem("user"));
  return { Authorization: "Bearer " + user?.Token, Accept: "*/*" };
}

const hariIni = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Posisi awal bagi room yang belum pernah disusun: baris-baris grid rapi.
function posisiAwal(index) {
  const kolom = 6;
  return { x: 20 + (index % kolom) * (CARD_W + 16), y: 20 + Math.floor(index / kolom) * (CARD_H + 16) };
}

function warnaRoom(penghuni) {
  const ada = (j) => penghuni.some((p) => p.Jenis === j);
  if (ada("KARYAWAN") && ada("KLIEN")) return { bg: "#fef3c7", border: "#f59e0b", label: "Campuran" };
  if (ada("KARYAWAN")) return { bg: "#dbeafe", border: "#3b82f6", label: "Karyawan" };
  if (ada("KLIEN")) return { bg: "#dcfce7", border: "#22c55e", label: "Klien" };
  return { bg: "#f3f4f6", border: "#d1d5db", label: "Kosong" };
}

const LEGENDA = [
  { bg: "#f3f4f6", border: "#d1d5db", t: "Kosong" },
  { bg: "#dbeafe", border: "#3b82f6", t: "Karyawan" },
  { bg: "#dcfce7", border: "#22c55e", t: "Klien" },
  { bg: "#fef3c7", border: "#f59e0b", t: "Campuran" },
];

export default function AkomodasiPage() {
  const [tanggal, setTanggal] = useState(hariIni());
  const [data, setData] = useState(null);
  const [posisi, setPosisi] = useState({}); // RoomID -> {x,y,tampil}
  const [edit, setEdit] = useState(false);
  const [modalPaket, setModalPaket] = useState(false);
  const [sibukPaket, setSibukPaket] = useState(false);
  const [pesanPaket, setPesanPaket] = useState("");
  const [blok, setBlok] = useState([]); // [{id,nama,x,y,w,h,warna}] — id lokal
  const [pilih, setPilih] = useState(null); // RoomID yang dibuka panelnya
  const [error, setError] = useState("");
  const [simpan, setSimpan] = useState(false);
  const drag = useRef(null);
  const kanvasRef = useRef(null);
  const [zoom, setZoom] = useState(null); // null = belum diatur -> otomatis muat lebar

  const muat = useCallback(async (tgl) => {
    setError("");
    try {
      const res = await axios.get(API + "getAkomodasi", { headers: headers(), params: { tanggal: tgl } });
      const d = res.data.data;
      setData(d);
      setBlok(
        (d.Blok || []).map((b, i) => ({ id: i + 1, nama: b.NamaBlok, x: b.PosX, y: b.PosY, w: b.Lebar, h: b.Tinggi, warna: b.Warna }))
      );
      const p = {};
      let nBaru = 0;
      d.Room.forEach((r) => {
        if (r.AdaPosisi) p[r.RoomID] = { x: r.PosX, y: r.PosY, tampil: r.Tampil };
        else {
          const a = posisiAwal(nBaru++);
          p[r.RoomID] = { x: a.x, y: a.y, tampil: true };
        }
      });
      setPosisi(p);
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.Message || "Gagal memuat data akomodasi");
    }
  }, []);

  useEffect(() => {
    muat(tanggal);
  }, [tanggal, muat]);

  // ── seleksi & drag (hanya mode Atur Denah) ──
  // Klik item = pilih satu; Shift/Ctrl/Cmd+klik = tambah/lepas; tarik di area kosong = kotak
  // seleksi (marquee); item terpilih digeser bersama (mouse atau tombol panah, Esc = batal pilih).
  const [selRoom, setSelRoom] = useState({});
  const [selBlok, setSelBlok] = useState({});
  const [marquee, setMarquee] = useState(null); // {x,y,w,h} koordinat kanvas (skala 100%)
  // Identitas state dipertahankan bila sudah kosong, agar efek tanpa dependensi tidak memicu render berulang.
  const kosongkanSel = () => {
    setSelRoom((s0) => (Object.keys(s0).length ? {} : s0));
    setSelBlok((s0) => (Object.keys(s0).length ? {} : s0));
  };
  const jumlahSel = Object.keys(selRoom).length + Object.keys(selBlok).length;
  const snap = (v) => Math.round(v / SNAP) * SNAP;

  // Room yang ikut bergerak: terpilih langsung + room yang berada di dalam blok terpilih.
  const roomIkut = (sr, sb) => {
    const ids = new Set(Object.keys(sr));
    blok.filter((b) => sb[b.id]).forEach((b) => {
      data.Room.forEach((r) => {
        if (posisi[r.RoomID] && didalamBlok(posisi[r.RoomID], b)) ids.add(r.RoomID);
      });
    });
    return ids;
  };
  const asalGrup = (sr, sb) => {
    const rooms = {};
    roomIkut(sr, sb).forEach((rid) => {
      rooms[rid] = { x: posisi[rid].x, y: posisi[rid].y };
    });
    const bloks = {};
    blok.forEach((b) => {
      if (sb[b.id]) bloks[b.id] = { x: b.x, y: b.y };
    });
    return { rooms, bloks };
  };
  // Geser seluruh grup dari posisi asalnya sebesar (dx,dy); tidak boleh keluar dari kiri/atas kanvas.
  const terapkanGeser = (g, dx, dy) => {
    const semua = [...Object.values(g.rooms), ...Object.values(g.bloks)];
    if (semua.length === 0) return;
    const mx = Math.max(dx, -Math.min(...semua.map((o) => o.x)));
    const my = Math.max(dy, -Math.min(...semua.map((o) => o.y)));
    setPosisi((p) => {
      const n = { ...p };
      Object.entries(g.rooms).forEach(([rid, o]) => {
        n[rid] = { ...n[rid], x: o.x + mx, y: o.y + my };
      });
      return n;
    });
    setBlok((bs) => bs.map((b) => (g.bloks[b.id] ? { ...b, x: g.bloks[b.id].x + mx, y: g.bloks[b.id].y + my } : b)));
  };

  const mulaiItem = (e, jenis, id) => {
    if (!edit) return;
    e.preventDefault();
    e.stopPropagation();
    const peta = jenis === "room" ? selRoom : selBlok;
    if (e.shiftKey || e.ctrlKey || e.metaKey) {
      const set = jenis === "room" ? setSelRoom : setSelBlok;
      set((s0) => {
        const n = { ...s0 };
        if (n[id]) delete n[id];
        else n[id] = true;
        return n;
      });
      return;
    }
    let sr = selRoom;
    let sb = selBlok;
    if (!peta[id]) {
      sr = jenis === "room" ? { [id]: true } : {};
      sb = jenis === "blok" ? { [id]: true } : {};
      setSelRoom(sr);
      setSelBlok(sb);
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { tipe: "grup", sx: e.clientX, sy: e.clientY, z: zoom || 1, ...asalGrup(sr, sb) };
  };
  const mulaiMarquee = (e) => {
    if (!edit || e.target !== e.currentTarget) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const z0 = zoom || 1;
    const x = (e.clientX - rect.left) / z0;
    const y = (e.clientY - rect.top) / z0;
    const tambah = e.shiftKey || e.ctrlKey || e.metaKey;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { tipe: "marquee", x0: x, y0: y, rect, z: z0, awalRoom: tambah ? selRoom : {}, awalBlok: tambah ? selBlok : {} };
    if (!tambah) kosongkanSel();
    setMarquee({ x, y, w: 0, h: 0 });
  };
  const gerakDrag = (e) => {
    const d = drag.current;
    if (!d) return;
    if (d.tipe === "grup") {
      terapkanGeser(d, snap((e.clientX - d.sx) / d.z), snap((e.clientY - d.sy) / d.z));
    } else if (d.tipe === "ukuran") {
      const dx = (e.clientX - d.sx) / d.z;
      const dy = (e.clientY - d.sy) / d.z;
      const w = Math.max(BLOK_MIN, snap(d.ow + dx));
      const h = Math.max(BLOK_MIN, snap(d.oh + dy));
      setBlok((bs) => bs.map((b) => (b.id === d.id ? { ...b, w, h } : b)));
    } else if (d.tipe === "marquee") {
      const x = (e.clientX - d.rect.left) / d.z;
      const y = (e.clientY - d.rect.top) / d.z;
      const m = { x: Math.min(x, d.x0), y: Math.min(y, d.y0), w: Math.abs(x - d.x0), h: Math.abs(y - d.y0) };
      setMarquee(m);
      const sr = { ...d.awalRoom };
      data.Room.forEach((r) => {
        const pos = posisi[r.RoomID];
        if (pos && pos.x < m.x + m.w && pos.x + CARD_W > m.x && pos.y < m.y + m.h && pos.y + CARD_H > m.y) sr[r.RoomID] = true;
      });
      const sb = { ...d.awalBlok };
      blok.forEach((b) => {
        if (b.x >= m.x && b.y >= m.y && b.x + b.w <= m.x + m.w && b.y + b.h <= m.y + m.h) sb[b.id] = true;
      });
      setSelRoom(sr);
      setSelBlok(sb);
    }
  };
  const mulaiUkuran = (e, b) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { tipe: "ukuran", id: b.id, sx: e.clientX, sy: e.clientY, ow: b.w, oh: b.h, z: zoom || 1 };
  };
  const ubahBlok = (id, patch) => setBlok((bs) => bs.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const hapusBlok = (id) => setBlok((bs) => bs.filter((b) => b.id !== id));
  const tambahBlok = () => {
    const id = Math.max(0, ...blok.map((b) => b.id)) + 1;
    // Taruh di bawah seluruh isi denah, supaya tidak menimpa room/blok lain;
    // setelah itu geser ke tempatnya.
    const x = 20;
    const y = snap(Math.max(0, tinggi - 20));
    setBlok((bs) => [...bs, { id, nama: "Blok " + id, x, y, w: 400, h: 260, warna: "abu" }]);
  };
  const akhiriDrag = () => {
    drag.current = null;
    setMarquee(null);
  };
  const pilihSemua = () => {
    const sr = {};
    data.Room.forEach((r) => {
      if (posisi[r.RoomID]?.tampil) sr[r.RoomID] = true;
    });
    const sb = {};
    blok.forEach((b) => {
      sb[b.id] = true;
    });
    setSelRoom(sr);
    setSelBlok(sb);
  };
  // Panah = geser item terpilih 1 langkah (Shift = 5 langkah); Esc = batal pilih.
  useEffect(() => {
    if (!edit) {
      kosongkanSel();
      return undefined;
    }
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "select" || tag === "textarea") return;
      if (e.key === "Escape") {
        kosongkanSel();
        return;
      }
      const arah = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
      if (!arah || jumlahSel === 0 || !data) return;
      e.preventDefault();
      const step = SNAP * (e.shiftKey ? 5 : 1);
      terapkanGeser(asalGrup(selRoom, selBlok), arah[0] * step, arah[1] * step);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const simpanDenah = async () => {
    setSimpan(true);
    try {
      const body = {
        Room: data.Room.map((r) => ({
          RoomID: r.RoomID,
          PosX: posisi[r.RoomID].x,
          PosY: posisi[r.RoomID].y,
          Tampil: posisi[r.RoomID].tampil,
        })),
        Blok: blok.map((b) => ({ NamaBlok: b.nama, PosX: b.x, PosY: b.y, Lebar: b.w, Tinggi: b.h, Warna: b.warna })),
      };
      await axios.post(API + "saveAkomodasiLayout", body, { headers: headers() });
      setEdit(false);
      await muat(tanggal);
    } catch (e) {
      setError(e.response?.data?.message || "Gagal menyimpan denah");
    }
    setSimpan(false);
  };

  const simpanPaketBanyak = async (jenisIds) => {
    setSibukPaket(true);
    setPesanPaket("");
    try {
      await axios.post(API + "saveAkomodasiRoomLayanan", { RoomIDs: Object.keys(selRoom), JenisIDs: jenisIds }, { headers: headers() });
      setModalPaket(false);
      // Muat ulang data servis tanpa membuang susunan denah yang sedang diedit.
      const res = await axios.get(API + "getAkomodasi", { headers: headers(), params: { tanggal } });
      const baru = res.data.data;
      setData((d) => ({ ...d, Room: d.Room.map((r) => ({ ...r, Layanan: baru.Room.find((x) => x.RoomID === r.RoomID)?.Layanan || [] })) }));
    } catch (e) {
      setPesanPaket(pesanError(e, "Gagal menyimpan paket servis"));
    }
    setSibukPaket(false);
  };

  const batalEdit = async () => {
    setEdit(false);
    await muat(tanggal);
  };

  const toggleTampil = (id) => setPosisi((p) => ({ ...p, [id]: { ...p[id], tampil: !p[id].tampil } }));

  // Ukuran kanvas (px, skala 100%) dari posisi room yang tampil.
  const roomTampil = data ? data.Room.filter((r) => edit || posisi[r.RoomID]?.tampil) : [];
  const lebar = Math.max(400, ...roomTampil.map((r) => posisi[r.RoomID].x + CARD_W + 40), ...blok.map((b) => b.x + b.w + 40));
  const tinggi = Math.max(300, ...roomTampil.map((r) => posisi[r.RoomID].y + CARD_H + 40), ...blok.map((b) => b.y + b.h + 40));

  const hitungMuat = () => {
    const w = kanvasRef.current?.clientWidth || lebar;
    return Math.min(1.5, Math.max(ZOOM_MIN, Math.floor((w / lebar) * 100) / 100));
  };
  // Pertama kali data tampil: otomatis muat selebar kotak denah (tidak diperbesar > 100%).
  useEffect(() => {
    if (data && zoom === null) setZoom(Math.min(1, hitungMuat()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  const atur = (z) => setZoom(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 100) / 100)));
  const z = zoom || 1;

  if (!data) {
    return (
      <div className="md:ml-64 p-6 text-sm text-gray-600">
        {error ? <span className="text-red-600">{error}</span> : "Memuat..."}
      </div>
    );
  }

  const rooms = roomTampil;
  const roomTerpilih = data.Room.find((r) => r.RoomID === pilih);
  const totalTerisi = data.Room.filter((r) => posisi[r.RoomID]?.tampil && r.Penghuni.length > 0).length;
  const totalTampil = data.Room.filter((r) => posisi[r.RoomID]?.tampil).length;

  return (
    <div className="md:ml-64 p-4">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h1 className="text-lg font-semibold text-gray-800">Akomodasi</h1>
        <label className="text-xs font-semibold text-gray-600 uppercase ml-2">Tanggal</label>
        <input
          type="date"
          value={tanggal}
          onChange={(e) => e.target.value && setTanggal(e.target.value)}
          className="border border-gray-300 rounded px-2 py-1 text-sm outline-none focus:border-blue-400"
        />
        <span className="text-sm text-gray-600">
          {totalTerisi} dari {totalTampil} room terisi
        </span>
        <div className="flex-1" />
        <div className="flex items-center gap-1" title="Perbesar / perkecil denah">
          <button type="button" onClick={() => atur(z - 0.1)} className="border border-gray-300 rounded w-8 h-9 text-lg leading-none">
            −
          </button>
          <input
            type="range"
            min={ZOOM_MIN * 100}
            max={ZOOM_MAX * 100}
            step={5}
            value={Math.round(z * 100)}
            onChange={(e) => atur(Number(e.target.value) / 100)}
            className="w-28"
          />
          <button type="button" onClick={() => atur(z + 0.1)} className="border border-gray-300 rounded w-8 h-9 text-lg leading-none">
            +
          </button>
          <span className="text-xs text-gray-600 w-10 text-right">{Math.round(z * 100)}%</span>
          <button type="button" onClick={() => setZoom(hitungMuat())} className="border border-gray-300 rounded px-2 h-9 text-xs">
            Muat
          </button>
        </div>
        {!data.BolehIsi ? (
          <span className="text-xs text-gray-500">Hanya lihat (pengisian oleh tim HRGA/GA)</span>
        ) : !edit ? (
          <button
            type="button"
            onClick={() => setEdit(true)}
            style={{ borderColor: Colors.primary, color: Colors.primary }}
            className="border rounded px-3 h-9 text-sm hover:bg-gray-50"
          >
            Atur Denah
          </button>
        ) : (
          <>
            <span className="text-xs text-gray-600">{jumlahSel} dipilih</span>
            <button type="button" onClick={pilihSemua} className="border border-gray-300 rounded px-3 h-9 text-sm">
              Pilih semua
            </button>
            <button type="button" onClick={kosongkanSel} disabled={jumlahSel === 0} className="border border-gray-300 rounded px-3 h-9 text-sm disabled:opacity-40">
              Batal pilih
            </button>
            <button
              type="button"
              onClick={() => setModalPaket(true)}
              disabled={Object.keys(selRoom).length === 0}
              className="border border-gray-300 rounded px-3 h-9 text-sm disabled:opacity-40"
              title="Tetapkan paket servis untuk room terpilih"
            >
              Atur Servis ({Object.keys(selRoom).length})
            </button>
            <button type="button" onClick={tambahBlok} className="border border-gray-300 rounded px-3 h-9 text-sm">
              + Blok
            </button>
            <button type="button" onClick={batalEdit} className="border border-gray-300 rounded px-3 h-9 text-sm">
              Batal
            </button>
            <button
              type="button"
              disabled={simpan}
              onClick={simpanDenah}
              style={{ backgroundColor: Colors.primary }}
              className="text-white rounded px-3 h-9 text-sm disabled:opacity-60"
            >
              {simpan ? "Menyimpan..." : "Simpan Denah"}
            </button>
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-2 text-xs text-gray-700">
        {LEGENDA.map((l) => (
          <span key={l.t} className="flex items-center gap-1">
            <span style={{ background: l.bg, borderColor: l.border }} className="inline-block w-4 h-4 border-2 rounded" />
            {l.t}
          </span>
        ))}
        {edit && <span className="text-amber-700">Mode atur denah: klik item untuk memilih (Shift/Ctrl+klik = tambah), tarik di area kosong untuk memilih banyak sekaligus, lalu geser dengan mouse atau tombol panah. Blok: geser judulnya, tarik sudut kanan-bawah untuk mengubah ukuran.</span>}
      </div>
      {error && <div className="mb-2 text-sm text-red-600">{error}</div>}

      <div ref={kanvasRef} className="border border-gray-300 rounded overflow-auto bg-white" style={{ maxHeight: "70vh" }}>
        <div style={{ width: lebar * z, height: tinggi * z }}>
        <div
          onPointerDown={mulaiMarquee}
          onPointerMove={gerakDrag}
          onPointerUp={akhiriDrag}
          style={{
            position: "relative",
            width: lebar,
            height: tinggi,
            transform: `scale(${z})`,
            transformOrigin: "top left",
            backgroundImage: edit ? "radial-gradient(#d1d5db 1px, transparent 1px)" : "none",
            backgroundSize: `${SNAP * 2}px ${SNAP * 2}px`,
          }}
        >
          {blok.map((b) => {
            const w = WARNA_BLOK[b.warna] || WARNA_BLOK.abu;
            const isi = data.Room.filter((r) => posisi[r.RoomID]?.tampil && didalamBlok(posisi[r.RoomID], b));
            const terisi = isi.filter((r) => r.Penghuni.length > 0).length;
            return (
              <div
                key={"blok" + b.id}
                style={{
                  position: "absolute",
                  left: b.x,
                  top: b.y,
                  width: b.w,
                  height: b.h,
                  background: w.bg,
                  borderColor: w.border,
                  borderStyle: selBlok[b.id] ? "solid" : "dashed",
                  boxShadow: selBlok[b.id] ? "0 0 0 3px #2563eb" : "none",
                  pointerEvents: "none",
                }}
                className="border-2 rounded-xl"
              >
                <div
                  onPointerDown={(e) => mulaiItem(e, "blok", b.id)}
                  style={{
                    position: "relative",
                    zIndex: 2, // di atas kartu room (zIndex 1) supaya judul tetap bisa digeser
                    height: TINGGI_JUDUL_BLOK,
                    pointerEvents: edit ? "auto" : "none",
                    cursor: edit ? "grab" : "default",
                    touchAction: "none",
                    userSelect: "none",
                  }}
                  className="flex items-center gap-2 px-2"
                >
                  {edit ? (
                    <>
                      <input
                        value={b.nama}
                        onPointerDown={(e) => e.stopPropagation()}
                        onChange={(e) => ubahBlok(b.id, { nama: e.target.value })}
                        className="border border-gray-300 rounded px-1 text-sm font-semibold bg-white w-40"
                      />
                      <select
                        value={b.warna}
                        onPointerDown={(e) => e.stopPropagation()}
                        onChange={(e) => ubahBlok(b.id, { warna: e.target.value })}
                        className="border border-gray-300 rounded text-xs bg-white"
                      >
                        {Object.entries(WARNA_BLOK).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => window.confirm(`Hapus blok "${b.nama}"? Room di dalamnya tidak ikut terhapus.`) && hapusBlok(b.id)}
                        className="text-xs text-red-600 border border-red-300 rounded px-1 bg-white"
                      >
                        Hapus
                      </button>
                    </>
                  ) : (
                    <span className="text-sm font-semibold text-gray-700">{b.nama}</span>
                  )}
                  <span className="text-xs text-gray-600 ml-auto">
                    {terisi}/{isi.length} terisi
                  </span>
                </div>
                {edit && (
                  <div
                    onPointerDown={(e) => mulaiUkuran(e, b)}
                    title="Tarik untuk mengubah ukuran"
                    style={{
                      position: "absolute",
                      right: 0,
                      bottom: 0,
                      width: 18,
                      height: 18,
                      cursor: "nwse-resize",
                      zIndex: 2,
                      pointerEvents: "auto",
                      touchAction: "none",
                      borderRight: "4px solid " + w.border,
                      borderBottom: "4px solid " + w.border,
                      borderBottomRightRadius: 10,
                    }}
                  />
                )}
              </div>
            );
          })}
          {rooms.map((r) => {
            const pos = posisi[r.RoomID];
            const w = warnaRoom(r.Penghuni);
            const redup = edit && !pos.tampil;
            return (
              <div
                key={r.RoomID}
                onPointerDown={(e) => mulaiItem(e, "room", r.RoomID)}
                onClick={() => !edit && setPilih(r.RoomID)}
                title={r.NamaRoom}
                style={{
                  position: "absolute",
                  left: pos.x,
                  top: pos.y,
                  width: CARD_W,
                  height: CARD_H,
                  background: w.bg,
                  borderColor: w.border,
                  opacity: redup ? 0.4 : 1,
                  cursor: edit ? "grab" : "pointer",
                  touchAction: "none",
                  userSelect: "none",
                  zIndex: 1,
                  boxShadow: selRoom[r.RoomID] ? "0 0 0 3px #2563eb" : undefined,
                }}
                className="border-2 rounded-lg p-2 overflow-hidden shadow-sm"
              >
                <div className="flex items-start justify-between gap-1">
                  <p className="text-sm font-semibold text-gray-800 leading-tight break-words">{r.NamaRoom}</p>
                  {edit && (
                    <button
                      type="button"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => toggleTampil(r.RoomID)}
                      className="text-xs text-gray-600 border border-gray-300 rounded px-1 bg-white"
                      title={pos.tampil ? "Sembunyikan dari denah" : "Tampilkan di denah"}
                    >
                      {pos.tampil ? "Tampil" : "Sembunyi"}
                    </button>
                  )}
                </div>
                {r.Layanan.length > 0 && (
                  <div className="flex gap-0.5 mt-0.5">
                    {r.Layanan.map((id) => {
                      const selesai = r.LayananHariIni.some((l) => l.JenisID === id && l.Tahap === "SELESAI");
                      return (
                        <span
                          key={id}
                          title={(data.JenisLayanan.find((j) => j.JenisID === id)?.Nama || id) + (selesai ? " — sudah dilayani" : " — belum dilayani")}
                          className={"text-[10px] leading-none rounded px-1 py-0.5 font-semibold " + (selesai ? "bg-green-500 text-white" : "bg-white text-amber-700 border border-amber-400")}
                        >
                          {SINGKAT[id] || id.slice(0, 1)}
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className="mt-1 text-xs text-gray-700 leading-tight">
                  {r.Penghuni.length === 0 ? (
                    <span className="text-gray-400">Kosong</span>
                  ) : (
                    <>
                      {r.Penghuni.slice(0, 2).map((p, i) => (
                        <div key={i} className="truncate">
                          {p.Nama}
                        </div>
                      ))}
                      {r.Penghuni.length > 2 && <div>+{r.Penghuni.length - 2} lainnya</div>}
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {marquee && (
            <div
              style={{
                position: "absolute",
                left: marquee.x,
                top: marquee.y,
                width: marquee.w,
                height: marquee.h,
                border: "1px dashed #2563eb",
                background: "rgba(37,99,235,0.12)",
                pointerEvents: "none",
                zIndex: 3,
              }}
            />
          )}
        </div>
        </div>
      </div>

      <GrafikServis data={data} roomTampil={data.Room.filter((r) => posisi[r.RoomID]?.tampil)} tanggal={tanggal} />

      {modalPaket && (
        <ModalPaketBanyak
          jumlahRoom={Object.keys(selRoom).length}
          jenis={data.JenisLayanan}
          onSimpan={simpanPaketBanyak}
          onTutup={() => setModalPaket(false)}
          sibuk={sibukPaket}
          pesan={pesanPaket}
        />
      )}

      {roomTerpilih && (
        <PanelRoom
          room={roomTerpilih}
          tanggal={tanggal}
          opsi={data}
          onTutup={() => setPilih(null)}
          onUbah={() => muat(tanggal)}
        />
      )}
    </div>
  );
}

// Panel penghuni satu room: daftar penghuni pada tanggal terpilih + form check-in manual.
function PanelRoom({ room, tanggal, opsi, onTutup, onUbah }) {
  const [form, setForm] = useState({
    Jenis: "KARYAWAN",
    Nik: "",
    KlienID: "",
    JrID: "",
    NamaPenghuni: "",
    TanggalMasuk: tanggal,
    TanggalKeluar: "",
    // Penghuni klien: hari terakhir menginap (default akhir periode JR = akhir bulan), bukan "tanggal keluar".
    TanggalAkhir: "",
    Catatan: "",
  });
  const [pesan, setPesan] = useState("");
  const [sibuk, setSibuk] = useState(false);
  const [fotoBesar, setFotoBesar] = useState(null);
  const [sibukPaket, setSibukPaket] = useState(false);
  const [pesanPaket, setPesanPaket] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // JR General Service milik klien terpilih. Room yang sudah ditugaskan ke satu JR terkunci ke JR itu
  // (satu room = satu klien = satu JR); bila hanya ada satu pilihan, otomatis terpilih.
  const roomDikuasaiKlien = room.Penghuni.some((p) => p.Jenis === "KLIEN");
  const klienRoom = roomDikuasaiKlien ? room.KlienID || "" : "";
  const jrRoom = room.Penghuni.find((p) => p.Jenis === "KLIEN" && p.JrID)?.JrID || "";
  const jrKlien = useMemo(
    () => (form.KlienID ? (opsi.JrAktif || []).filter((j) => j.KlienID === form.KlienID && (!jrRoom || j.JrID === jrRoom)) : []),
    [opsi.JrAktif, form.KlienID, jrRoom]
  );
  useEffect(() => {
    if (form.Jenis !== "KLIEN") return;
    setForm((f) => {
      if (jrKlien.length === 1) return f.JrID === jrKlien[0].JrID ? f : { ...f, JrID: jrKlien[0].JrID };
      return jrKlien.some((j) => j.JrID === f.JrID) ? f : { ...f, JrID: "" };
    });
  }, [jrKlien, form.Jenis]);
  const jrPilih = jrKlien.find((j) => j.JrID === form.JrID);
  // Tanggal masuk/keluar dibatasi periode JR dan masa berlaku Quotation (BatasKeluar eksklusif).
  const hariSebelum = (d) => {
    const t = new Date(d + "T00:00:00Z");
    t.setUTCDate(t.getUTCDate() - 1);
    return t.toISOString().slice(0, 10);
  };
  useEffect(() => {
    if (!jrPilih) return;
    setForm((f) => {
      const masuk = f.TanggalMasuk < jrPilih.Mulai ? jrPilih.Mulai : f.TanggalMasuk >= jrPilih.BatasKeluar ? hariSebelum(jrPilih.BatasKeluar) : f.TanggalMasuk;
      // Tanggal akhir: default = akhir periode JR (akhir bulan); dijepit ke [masuk, akhir periode].
      let akhir = f.TanggalAkhir && f.TanggalAkhir <= jrPilih.Akhir ? f.TanggalAkhir : jrPilih.Akhir;
      if (akhir < masuk) akhir = masuk;
      return masuk === f.TanggalMasuk && akhir === f.TanggalAkhir ? f : { ...f, TanggalMasuk: masuk, TanggalAkhir: akhir };
    });
  }, [jrPilih]);
  const kapasitas = room.Kapasitas || jrPilih?.Kapasitas || 0;
  const roomPenuh = roomDikuasaiKlien && kapasitas > 0 && room.Penghuni.length >= kapasitas;
  useEffect(() => {
    if (roomDikuasaiKlien) setForm((f) => (f.Jenis === "KLIEN" && (!klienRoom || f.KlienID === klienRoom) ? f : { ...f, Jenis: "KLIEN", Nik: "", KlienID: klienRoom || f.KlienID }));
  }, [roomDikuasaiKlien, klienRoom]);
  const namaJenis = (id) => opsi.JenisLayanan.find((j) => j.JenisID === id)?.Nama || id;

  const simpanPaket = async (jenisIds) => {
    setSibukPaket(true);
    setPesanPaket("");
    try {
      await axios.post(API + "saveAkomodasiRoomLayanan", { RoomIDs: [room.RoomID], JenisIDs: jenisIds }, { headers: headers() });
      await onUbah();
    } catch (e) {
      setPesanPaket(pesanError(e, "Gagal menyimpan paket servis"));
    }
    setSibukPaket(false);
  };

  const kirim = async (url, body, asForm) => {
    setSibuk(true);
    setPesan("");
    try {
      const payload = asForm ? new URLSearchParams(body) : body;
      await axios.post(API + url, payload, { headers: headers() });
      await onUbah();
      return true;
    } catch (e) {
      setPesan(e.response?.data?.message || e.response?.data?.Message || "Gagal menyimpan");
      return false;
    } finally {
      setSibuk(false);
    }
  };

  const simpanPenghuni = async (e) => {
    e.preventDefault();
    const ok = await kirim("insertAkomodasiPenghuni", { ...form, TanggalKeluar: form.Jenis === "KLIEN" ? "" : form.TanggalKeluar, TanggalAkhir: form.Jenis === "KLIEN" ? form.TanggalAkhir : "", RoomID: room.RoomID }, false);
    if (ok) setForm((f) => ({ ...f, Nik: "", KlienID: "", JrID: "", NamaPenghuni: "", TanggalKeluar: "", TanggalAkhir: "", Catatan: "" }));
  };

  const checkout = (p) => {
    const tgl = window.prompt("Tanggal keluar (YYYY-MM-DD):", tanggal);
    if (tgl) kirim("checkoutAkomodasiPenghuni", { PenghuniID: p.PenghuniID, TanggalKeluar: tgl }, true);
  };
  const hapus = (p) => {
    if (window.confirm(`Hapus catatan penghuni "${p.Nama}"?`)) kirim("deleteAkomodasiPenghuni", { PenghuniID: p.PenghuniID }, true);
  };

  const inputCls = "border border-gray-300 rounded px-2 py-1 text-sm w-full outline-none focus:border-blue-400";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onTutup}>
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] overflow-auto p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-base font-semibold text-gray-800">Room {room.NamaRoom}</h2>
          <button type="button" onClick={onTutup} className="text-gray-500 text-xl leading-none">
            ×
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-3">Penghuni pada {tanggal}</p>
        {room.PaketTerkunci && room.Kapasitas > 0 && (
          <p className={"text-xs rounded px-2 py-1 mb-3 border " + (room.Penghuni.length >= room.Kapasitas ? "bg-red-50 text-red-700 border-red-200" : "bg-blue-50 text-blue-800 border-blue-200")}>
            Kapasitas room (sesuai Quotation): terisi {room.Penghuni.length} dari {room.Kapasitas} orang
            {room.Penghuni.length >= room.Kapasitas ? " — PENUH" : ` · sisa ${room.Kapasitas - room.Penghuni.length}`}
          </p>
        )}

        {room.Penghuni.length === 0 ? (
          <p className="text-sm text-gray-500 mb-3">Room kosong pada tanggal ini.</p>
        ) : (
          <ul className="mb-3 divide-y border rounded">
            {room.Penghuni.map((p, i) => (
              <li key={i} className="p-2 text-sm flex items-start gap-2">
                <div className="flex-1">
                  <div className="font-medium text-gray-800">{p.Nama}</div>
                  <div className="text-xs text-gray-600">
                    <span
                      className={
                        "inline-block rounded px-1.5 mr-1 " +
                        (p.Jenis === "KARYAWAN" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800")
                      }
                    >
                      {p.Jenis === "KARYAWAN" ? "Karyawan" : "Klien"}
                    </span>
                    {p.Klien && p.Jenis === "KLIEN" && p.Klien !== p.Nama ? p.Klien + " · " : ""}
                    {p.Sumber === "MANUAL" && p.JrID ? `JR ${p.JrID} · ` : ""}
                    {p.Sumber === "JR"
                      ? `dari JR ${p.JrID}`
                      : p.JrID && p.TanggalAkhir
                        ? `masuk ${p.TanggalMasuk}, tanggal akhir ${p.TanggalAkhir}`
                        : `masuk ${p.TanggalMasuk}${p.TanggalKeluar ? ", keluar " + p.TanggalKeluar : ""}`}
                  </div>
                  {p.Catatan && <div className="text-xs text-gray-500">{p.Catatan}</div>}
                </div>
                {p.Sumber === "MANUAL" && opsi.BolehIsi && (
                  <div className="flex gap-1">
                    <button type="button" disabled={sibuk} onClick={() => checkout(p)} className="text-xs border rounded px-2 py-0.5">
                      Check-out
                    </button>
                    <button type="button" disabled={sibuk} onClick={() => hapus(p)} className="text-xs border border-red-300 text-red-600 rounded px-2 py-0.5">
                      Hapus
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {opsi.BolehIsi && (
        <form onSubmit={simpanPenghuni} className="border-t pt-3 space-y-2">
          <p className="text-xs font-semibold text-gray-600 uppercase">Tambah penghuni (check-in)</p>
          {roomDikuasaiKlien && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              Room ini dihuni klien — penghuni tambahan harus klien yang sama
              {kapasitas > 0 ? ` (terisi ${room.Penghuni.length} dari maks. ${kapasitas} orang per room menurut Quotation)` : " (kapasitas belum diisi di Quotation, tidak dibatasi)"}.
            </p>
          )}
          <select value={form.Jenis} disabled={roomDikuasaiKlien} onChange={(e) => set("Jenis", e.target.value)} className={inputCls}>
            <option value="KARYAWAN" disabled={roomDikuasaiKlien}>Karyawan</option>
            <option value="KLIEN">Klien</option>
          </select>
          {form.Jenis === "KARYAWAN" ? (
            <select required value={form.Nik} onChange={(e) => set("Nik", e.target.value)} className={inputCls}>
              <option value="">— pilih karyawan —</option>
              {opsi.Karyawan.map((k) => (
                <option key={k.ID} value={k.ID}>
                  {k.Nama}
                </option>
              ))}
            </select>
          ) : (
            <>
              <select required value={form.KlienID} disabled={!!klienRoom} onChange={(e) => set("KlienID", e.target.value)} className={inputCls}>
                <option value="">— pilih perusahaan klien —</option>
                {opsi.Klien.map((k) => (
                  <option key={k.ID} value={k.ID}>
                    {k.Nama}
                  </option>
                ))}
              </select>
              {form.KlienID && jrKlien.length === 0 && (
                <p className="text-xs text-red-600">
                  {jrRoom
                    ? "JR room ini tidak lagi tersedia untuk penugasan (status JR terkunci)."
                    : "Klien ini tidak punya JR General Service (Draft/Menunggu Activity) yang memuat layanan Room pada periode berjalan. Check-in tidak dapat disimpan."}
                </p>
              )}
              {jrKlien.length > 1 && (
                <select required value={form.JrID} onChange={(e) => set("JrID", e.target.value)} className={inputCls}>
                  <option value="">— pilih JR —</option>
                  {jrKlien.map((j) => (
                    <option key={j.JrID} value={j.JrID}>
                      {j.JrID} · {j.QoNo} · periode {j.Period}
                    </option>
                  ))}
                </select>
              )}
              {jrPilih && (
                <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">
                  JR {jrPilih.JrID} · QO {jrPilih.QoNo} · periode {jrPilih.Period} · kapasitas {jrPilih.Kapasitas > 0 ? `${jrPilih.Kapasitas} orang/room` : "belum diisi"} ·
                  layanan wajib: Pembersihan{jrPilih.Layanan.length ? `, tambahan (terkunci): ${jrPilih.Layanan.map(namaJenis).join(", ")}` : ""}
                </p>
              )}
              <input
                value={form.NamaPenghuni}
                onChange={(e) => set("NamaPenghuni", e.target.value)}
                placeholder="Nama penghuni (kosongkan bila sama dengan nama klien)"
                className={inputCls}
              />
            </>
          )}
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-gray-600">
              Tanggal masuk
              <input type="date" required value={form.TanggalMasuk}
                min={jrPilih?.Mulai} max={jrPilih ? hariSebelum(jrPilih.BatasKeluar) : undefined}
                onChange={(e) => setForm((f) => ({ ...f, TanggalMasuk: e.target.value, TanggalAkhir: f.TanggalAkhir && f.TanggalAkhir < e.target.value ? e.target.value : f.TanggalAkhir }))}
                className={inputCls} />
            </label>
            <label className="text-xs text-gray-600">
              {form.Jenis === "KLIEN" ? (
                <>
                  Tanggal akhir (JR)
                  <input type="date" required value={form.TanggalAkhir} min={form.TanggalMasuk} max={jrPilih?.Akhir}
                    onChange={(e) => set("TanggalAkhir", e.target.value)} className={inputCls} />
                </>
              ) : (
                <>
                  Tanggal keluar (opsional)
                  <input type="date" value={form.TanggalKeluar} min={form.TanggalMasuk}
                    onChange={(e) => set("TanggalKeluar", e.target.value)} className={inputCls} />
                </>
              )}
            </label>
          </div>
          <input value={form.Catatan} onChange={(e) => set("Catatan", e.target.value)} placeholder="Catatan (opsional)" className={inputCls} />
          {pesan && <p className="text-sm text-red-600">{pesan}</p>}
          <button
            type="submit"
            disabled={sibuk || roomPenuh || (form.Jenis === "KLIEN" && !form.JrID)}
            style={{ backgroundColor: Colors.primary }}
            className="text-white rounded px-3 h-9 text-sm w-full disabled:opacity-60"
          >
            {sibuk ? "Menyimpan..." : roomPenuh ? "Room penuh (sesuai Quotation)" : "Simpan Penghuni"}
          </button>
        </form>
        )}

        <div className="border-t mt-4 pt-3">
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Layanan room</h3>
          {room.PaketTerkunci && (
            <div className="mb-3 text-sm border border-green-200 bg-green-50 rounded px-3 py-2">
              <p className="text-xs font-semibold text-green-800 uppercase mb-1">Layanan room klien (terkunci, mengikuti JR)</p>
              <p className="text-gray-800">
                <span className="font-medium">Wajib:</span> Pembersihan Room
                {" · "}
                <span className="font-medium">Tambahan (JR):</span>{" "}
                {room.Layanan.filter((id) => id !== "BERSIH").map(namaJenis).join(", ") || "tidak ada (JR tidak memuat Laundry / Meals)"}
              </p>
            </div>
          )}
          {opsi.BolehIsi && !room.PaketTerkunci && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Servis yang diberikan di room ini</p>
              <PaketServisEditor jenis={opsi.JenisLayanan} awal={room.Layanan} onSimpan={simpanPaket} sibuk={sibukPaket} />
              {pesanPaket && <p className="text-sm text-red-600 mt-1">{pesanPaket}</p>}
            </div>
          )}
          <p className="text-xs text-gray-500">
            Menu ini hanya untuk menentukan penghuni dan mengunci layanan room. Aktivitas harian (kebersihan, meal, laundry)
            dicatat di aplikasi Akomodasi (<a href="/akomodasi/" className="text-blue-600 underline">/akomodasi/</a>) dan otomatis masuk ke JR.
          </p>
        </div>
      </div>
      <Lightbox url={fotoBesar} onTutup={() => setFotoBesar(null)} />
    </div>
  );
}
